import * as Yup from 'yup';
import { format as formatDate } from 'date-fns';
import debounce from 'debounce-promise';

import { CollapseSection } from '@/components/ats';
import {
  fetchAccountUsersSearch,
  accountUserFullName,
} from '@/api/useAccountUsers';

import type { DarwinboxFormField } from './api';

// Darwinbox rejects dates not in its DD-MM-YYYY (dashes) default format.
const DARWINBOX_DATE_OUTPUT_FORMAT = 'dd-MM-yyyy';

// react-select drops empty-string option values; hold a sentinel, decode at submit.
const EMPTY_CODE_PREFIX = '__darwinbox_empty_code__::';

// Match the lighter select placeholder weight on inputs.
const INPUT_PLACEHOLDER_STYLES = { fontWeight: 'normal' };

const sectionWrapperProps = {
  title: 'EMPLOYEE DETAILS',
  isHeaderClickable: true,
  defaultIsOpen: true,
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: { color: 'ats-primary' },
    iconStyles: { width: '24px', height: '24px' },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em',
    backgroundColor: 'white',
  },
};

// Manager field ships no options; offer account users and submit the selected email.
const isManagerField = (field: DarwinboxFormField) =>
  /manager\s*email/i.test(field.id) || /manager\s*email/i.test(field.label);

const loadAccountUserOptions = async (input: string) => {
  const { users } = await fetchAccountUsersSearch(input || '');
  return users
    .filter((user) => user.email)
    .map((user) => ({
      label: `${accountUserFullName(user)} — ${user.email}`,
      value: user.email,
    }));
};

// AsyncSelectField's debounce is a no-op; debounce at the source (one request per pause).
const debouncedLoadAccountUserOptions = debounce(loadAccountUserOptions, 300);

// Render 2-option Yes/No enums as a radio group.
const isYesNoOptions = (options: DarwinboxFormField['options']): boolean => {
  if (!Array.isArray(options) || options.length !== 2) return false;
  const labels = options.map((opt) => {
    const text = typeof opt === 'string' ? opt : opt.name;
    return (text || '').trim().toLowerCase();
  });
  return labels.includes('yes') && labels.includes('no');
};

const buildFieldConfig = (field: DarwinboxFormField) => {
  const base = {
    name: field.id,
    label: field.label,
    isRequired: field.required,
    placeholder: `Enter ${field.label}`,
    optionalValidation: true,
  } as Record<string, any>;

  // Real BE options should win over the account-user picker.
  if (isManagerField(field) && !field.options?.length) {
    const validation = field.required
      ? Yup.string().required(`${field.label} is required`)
      : Yup.string();
    return {
      ...base,
      type: 'asyncSelect',
      placeholder: 'Search team members...',
      valueKey: 'value',
      labelKey: 'label',
      isClearable: true,
      loadDefaultOptions: true,
      customLoadOptions: (input: string) =>
        debouncedLoadAccountUserOptions(input),
      // Seed the prefilled value so the chip renders before any search.
      ...(field.value
        ? {
            defaultOptions: [
              { label: String(field.value), value: String(field.value) },
            ],
          }
        : {}),
      validation,
    };
  }

  switch (field.type) {
    case 'string': {
      // Email fields get format validation (manager picker handled above).
      const isEmailField =
        /email/i.test(field.id) || /email/i.test(field.label);
      const stringSchema = isEmailField
        ? Yup.string().email('Enter a valid email address')
        : Yup.string();
      const validation = field.required
        ? stringSchema.required(`${field.label} is required`)
        : stringSchema;
      return {
        ...base,
        type: 'input',
        placeholderStyles: INPUT_PLACEHOLDER_STYLES,
        validation,
      };
    }
    case 'date': {
      const validation = field.required
        ? Yup.mixed().required(`${field.label} is required`)
        : Yup.mixed();
      return {
        ...base,
        type: 'date',
        // Format the picked Date; prefilled Dates are normalized at submit.
        dateFormat: (date: Date) =>
          date ? formatDate(date, DARWINBOX_DATE_OUTPUT_FORMAT) : '',
        validation,
      };
    }
    case 'enum': {
      // No options (Darwinbox unreachable / unbackfilled) — plain input.
      if (!field.options?.length) {
        const validation = field.required
          ? Yup.string().required(`${field.label} is required`)
          : Yup.string();
        return {
          ...base,
          type: 'input',
          placeholderStyles: INPUT_PLACEHOLDER_STYLES,
          validation,
        };
      }
      // Show the code on "*Code" fields to disambiguate; skip if the name already has it.
      const showCodeInLabel =
        /code/i.test(field.label) || /code/i.test(field.id);
      // Show name, submit code; blank codes use a sentinel.
      const options = field.options.map((opt) => {
        if (typeof opt === 'string') return { label: opt, value: opt };
        const hasCode = typeof opt.code === 'string' && opt.code !== '';
        const withCode =
          hasCode && showCodeInLabel && !opt.name.includes(opt.code);
        return {
          label: withCode ? `${opt.name} (${opt.code})` : opt.name,
          value: hasCode ? opt.code : `${EMPTY_CODE_PREFIX}${opt.name}`,
        };
      });
      const validation = field.required
        ? Yup.mixed().required(`${field.label} is required`)
        : Yup.mixed();
      if (isYesNoOptions(field.options)) {
        return {
          ...base,
          type: 'radio',
          options,
          radioOptionsDirection: 'row',
          validation,
        };
      }
      return {
        ...base,
        type: 'select',
        options,
        valueKey: 'value',
        labelKey: 'label',
        isClearable: true,
        validation,
      };
    }
    default:
      return null;
  }
};

const parseIsoToDate = (iso: string): Date | null => {
  // BE sends YYYY-MM-DD; build at local midnight to avoid TZ shifts.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
};

export interface DarwinboxConvertedSchema {
  formConfig: any[];
  initialValues: Record<string, any>;
}

export const convertDarwinboxSchemaToFormConfig = (
  fields: DarwinboxFormField[] = []
): DarwinboxConvertedSchema => {
  const initialValues: Record<string, any> = {};

  const fieldConfigs = fields
    .map((field) => {
      if (field.value !== null && field.value !== undefined) {
        if (field.type === 'date') {
          const dateObj = parseIsoToDate(String(field.value));
          if (dateObj) initialValues[field.id] = dateObj;
        } else {
          initialValues[field.id] = field.value;
        }
      }
      return buildFieldConfig(field);
    })
    .filter(Boolean);

  const formConfig = [
    {
      id: 'darwinbox-employee',
      type: 'wrapper',
      Wrapper: CollapseSection,
      wrapperProps: sectionWrapperProps,
      fields: fieldConfigs,
    },
  ];

  return { formConfig, initialValues };
};

// Normalize prefilled Dates and sentinel codes to the wire format Darwinbox expects.
export const normalizeDarwinboxValuesForSubmit = (
  values: Record<string, any>
): Record<string, any> => {
  const result: Record<string, any> = {};
  Object.entries(values).forEach(([key, raw]) => {
    if (raw instanceof Date) {
      result[key] = formatDate(raw, DARWINBOX_DATE_OUTPUT_FORMAT);
      return;
    }
    // Sentinel code -> real "".
    if (typeof raw === 'string' && raw.startsWith(EMPTY_CODE_PREFIX)) {
      result[key] = '';
      return;
    }
    result[key] = raw;
  });
  return result;
};
